// A simple disclosure menu: a button with aria-expanded that opens a list of
// links/buttons. Closes on Esc (focus back to the button), outside click, and
// after choosing an item.
import { useEffect, useId, useRef, useState, type ReactNode } from "react";

export function Menu({
  label,
  children,
  align = "right",
  className,
  look,
}: {
  label: ReactNode;
  children: ReactNode;
  align?: "left" | "right";
  /** Extra class on the wrapper: the header's menus name themselves (org-menu, avatar-menu). */
  className?: string;
  /** Draw the toggle as the design system's button (a page action), not the header's small control. */
  look?: "primary" | "secondary" | "quiet";
}) {
  const [open, setOpen] = useState(false);
  const wrapper = useRef<HTMLDivElement>(null);
  const button = useRef<HTMLButtonElement>(null);
  const listId = useId();

  useEffect(() => {
    if (!open) return;
    // Outside clicks are caught as pointerdown in the capture phase: a
    // bubbling mousedown never reaches the document when the target stops
    // it, and touch browsers (iOS Safari) send no mousedown at all for a tap
    // on a plain, non-clickable part of the page.
    const outside = (target: EventTarget | null) => !!wrapper.current && !(target instanceof Node && wrapper.current.contains(target));
    const onDown = (e: Event) => {
      if (outside(e.target)) setOpen(false);
    };
    // Tabbing out of the menu, or a click into an iframe (the window loses focus), closes it too.
    const onFocusIn = (e: FocusEvent) => {
      if (outside(e.target)) setOpen(false);
    };
    const onBlur = () => setOpen(false);
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        button.current?.focus();
      }
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("mousedown", onDown, true);
    document.addEventListener("focusin", onFocusIn);
    document.addEventListener("keydown", onKey);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("mousedown", onDown, true);
      document.removeEventListener("focusin", onFocusIn);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("blur", onBlur);
    };
  }, [open]);

  return (
    <div className={className ? `menu ${className}` : "menu"} ref={wrapper}>
      <button
        ref={button}
        type="button"
        className={look ? `btn btn--${look}` : "menu__button"}
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen((v) => !v)}
      >
        {label}
        <span aria-hidden="true" className="menu__caret">
          ▾
        </span>
      </button>
      <ul
        id={listId}
        className={`menu__list menu__list--${align}`}
        hidden={!open}
        onClick={(e) => {
          if ((e.target as HTMLElement).closest("a,button")) setOpen(false);
        }}
      >
        {children}
      </ul>
    </div>
  );
}
